import {
  captureException,
  captureMessage,
  startSpan,
  getActiveSpan,
  setUser,
} from '@sentry/browser';

export class HealthMetrics {
  static trackError(
    error: Error,
    context: {
      tags?: Record<string, string>;
      extra?: Record<string, any>;
      level?: 'fatal' | 'error' | 'warning' | 'info';
    } = {},
  ) {
    captureException(error, {
      tags: context.tags,
      extra: context.extra,
      level: context.level || 'error',
    });
  }

  static trackLogin(success: boolean, method: string = 'email') {
    startSpan(
      {
        name: 'User Login',
        op: 'auth.login',
        attributes: {
          'auth.result': success ? 'success' : 'failure',
          'auth.method': method,
        },
      },
      () => {
        if (!success) {
          captureMessage('Login failed', {
            level: 'warning',
            tags: {
              flow_type: 'authentication',
              outcome: 'failure',
            },
          });
        }
      },
    );
  }

  static trackCriticalFlow(
    flow: string,
    step: string,
    success: boolean,
    metadata?: Record<string, any>,
  ) {
    const span = getActiveSpan();
    if (span) {
      span.setAttribute(`${flow}.step`, step);
      span.setAttribute(`${flow}.success`, success);
    }

    if (!success) {
      captureMessage(`Critical flow failed: ${flow} at ${step}`, {
        level: 'error',
        tags: {
          critical_flow: flow,
          flow_step: step,
        },
        extra: metadata,
      });
    }
  }

  static trackAPIError(error: Error, endpoint: string, statusCode?: number) {
    // Determine error category based on status code
    const is5xx = statusCode && statusCode >= 500 && statusCode < 600;
    const is4xx = statusCode && statusCode >= 400 && statusCode < 500;

    // Detect integration from endpoint
    const integration = HealthMetrics.detectIntegration(endpoint);

    captureException(error, {
      tags: {
        error_type: 'api_error',
        endpoint: endpoint,
        status_code: statusCode?.toString(),
        // Add specific tags for dashboard filtering
        is_5xx: is5xx ? 'true' : 'false',
        is_4xx: is4xx ? 'true' : 'false',
        error_category: is5xx
          ? 'server_error'
          : is4xx
            ? 'client_error'
            : 'unknown',
        integration: integration,
      },
      contexts: {
        api: {
          endpoint,
          status_code: statusCode,
          timestamp: new Date().toISOString(),
          integration: integration,
        },
      },
      level: is5xx ? 'error' : 'warning',
    });
  }

  /**
   * Detect which integration/service an endpoint belongs to
   * This helps filter "Error Rate by Integration" in dashboard
   */
  private static detectIntegration(endpoint: string): string {
    const url = endpoint.toLowerCase();

    // Third-party integrations
    if (url.includes('plaid')) return 'plaid';
    if (url.includes('google')) return 'google';

    // Internal services by endpoint pattern
    if (
      url.includes('/auth') ||
      url.includes('/login') ||
      url.includes('/logout')
    )
      return 'auth';
    if (url.includes('/transaction')) return 'transactions';
    if (url.includes('/account') || url.includes('/bank-account'))
      return 'accounts';
    if (url.includes('/wire') || url.includes('/transfer')) return 'transfers';
    if (url.includes('/customer') || url.includes('/user')) return 'customers';
    if (url.includes('/application')) return 'applications';
    if (url.includes('/document') || url.includes('/upload'))
      return 'documents';
    if (url.includes('/company') || url.includes('/business'))
      return 'companies';
    if (url.includes('/teammate')) return 'teammates';

    return 'api';
  }

  static setUserContext(userId: string, email?: string, username?: string) {
    setUser({
      id: userId,
      email: email,
      username: username,
    });
  }

  static clearUserContext() {
    setUser(null);
  }
}
